import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Button, Input, Label, Textarea, Select, HelperText } from '../components/ui';
import { PageHeader } from '../components/PageHeader';
import { Check, X } from 'lucide-react';

// =============================================================================
// CapturePage
//
// Landing route used by the Chrome/Edge extension. The extension opens a new
// tab to /capture?name=...&url=...&sponsor=...&notes=...&source=extension_capture
// with the page details the user reviewed in the popup.
//
// Behavior:
//   - If the user isn't signed in, redirects to /signin?returnTo=/capture?...
//   - Otherwise, shows a form pre-filled with the URL params plus an applicant
//     selector (defaults to whoever is the current active applicant in the
//     ApplicantContext). User confirms/edits and saves to inbox.
//   - On success, navigates to /inbox so the user can see and triage.
// =============================================================================

function genId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function inboxItemFingerprint(name: string, sponsor: string): string {
  // Normalized hash used to dedup against existing items. Matches the
  // fingerprint logic used by the AI discovery import flow.
  const normalized = `${(name || '').toLowerCase().trim()}__${(sponsor || '').toLowerCase().trim()}`;
  return `cap_${normalized.replace(/[^a-z0-9]/g, '_').slice(0, 80)}`;
}

export function CapturePage() {
  const [params] = useSearchParams();
  const { user, membership } = useAuth();
  const { applicants, selectedApplicantId } = useApplicantContext();
  const nav = useNavigate();

  // v0.2.28: the page serves two cases. The extension opens it with URL params
  // pre-filled; a mobile/manual user opens it fresh with none. We detect which
  // by whether any capture param is present, and adjust copy + the saved
  // `source` value accordingly.
  //
  // v0.2.29: the Android PWA share target also lands here, with share_title /
  // share_text / share_url params. Android is inconsistent about which field
  // holds the link — sometimes share_url, often share_text contains the URL
  // (or the URL appended to other text). We normalize that below.
  const isShare =
    params.get('share_title') !== null ||
    params.get('share_text') !== null ||
    params.get('share_url') !== null;

  function extractSharedUrl(): string {
    const direct = params.get('share_url');
    if (direct && /^https?:\/\//i.test(direct)) return direct;
    // Pull the first URL out of share_text if present.
    const text = params.get('share_text') ?? '';
    const match = text.match(/https?:\/\/[^\s]+/);
    return match ? match[0] : (direct ?? '');
  }

  const isExtensionCapture =
    params.get('name') !== null ||
    params.get('url') !== null ||
    params.get('source') === 'extension_capture';

  // "Pre-filled" mode = extension OR share; "manual" = a fresh empty open.
  const isPrefilled = isExtensionCapture || isShare;

  const [name, setName] = useState(
    params.get('name') ?? params.get('share_title') ?? ''
  );
  const [url, setUrl] = useState(params.get('url') ?? extractSharedUrl());
  const [sponsor, setSponsor] = useState(params.get('sponsor') ?? '');
  const [notes, setNotes] = useState(params.get('notes') ?? '');
  // v0.2.19: extension popup now pre-parses these for known sites. URL params
  // come through as strings; we coerce to the InboxItem types on save.
  const [amountMin, setAmountMin] = useState(params.get('amount_min') ?? '');
  const [amountMax, setAmountMax] = useState(params.get('amount_max') ?? '');
  const [amountNotes, setAmountNotes] = useState(params.get('amount_notes') ?? '');
  const [deadlineApprox, setDeadlineApprox] = useState(params.get('deadline_approx') ?? '');
  const [deadlineRolling, setDeadlineRolling] = useState(params.get('deadline_rolling') === '1');
  const [requirementsSummary, setRequirementsSummary] = useState(params.get('requirements_summary') ?? '');
  const [applicantId, setApplicantId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default the applicant selector to whoever is currently active in the
  // global applicant switcher.
  useEffect(() => {
    if (selectedApplicantId && !applicantId) {
      setApplicantId(selectedApplicantId);
    } else if (!selectedApplicantId && applicants.length > 0 && !applicantId) {
      setApplicantId(applicants[0].applicant_id);
    }
  }, [selectedApplicantId, applicants, applicantId]);

  // If not signed in, bounce to signin and preserve the capture data.
  useEffect(() => {
    if (user === null) {
      const fullCapturePath = '/capture?' + params.toString();
      nav('/signin?returnTo=' + encodeURIComponent(fullCapturePath), { replace: true });
    }
  }, [user, nav, params]);

  async function handleSave() {
    if (!db || !membership || !user) {
      setError('Not ready — missing user or household context.');
      return;
    }
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!applicantId) {
      setError('Please pick which applicant this scholarship is for.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const id = genId('inbox');
      const fingerprint = inboxItemFingerprint(name, sponsor);
      // v0.2.19: extension popup may pre-fill amount/deadline/requirements
      // for recognized sites. Coerce strings to InboxItem field types.
      const parsedAmountMin = amountMin.trim() === '' ? null : Number(amountMin);
      const parsedAmountMax = amountMax.trim() === '' ? null : Number(amountMax);
      // Build payload matching the schema used by the AI discovery import path.
      // Fields that don't apply yet (match, tags, etc.) are left null/empty so
      // the user can fill them in during inbox review.
      await setDoc(doc(db, 'households', membership.householdId, 'inbox', id), {
        item_id: id,
        applicant_id: applicantId,
        source: isExtensionCapture ? 'extension_capture' : isShare ? 'share_capture' : 'manual_capture',
        discovery_run_id: null,
        name: name.trim(),
        sponsor: sponsor.trim(),
        url: url.trim() || null,
        url_verified: false,
        amount_min: Number.isFinite(parsedAmountMin) ? parsedAmountMin : null,
        amount_max: Number.isFinite(parsedAmountMax) ? parsedAmountMax : null,
        amount_notes: amountNotes.trim() || null,
        deadline_approx: deadlineApprox.trim() || null,
        deadline_rolling: deadlineRolling,
        match_strength: null,
        why_match: null,
        competitiveness: null,
        effort: null,
        tags: [],
        requirements_summary: requirementsSummary.trim() || null,
        need_based: false,
        homeschool_friendly: null,
        fingerprint,
        status: 'new',
        snooze_until: null,
        promoted_to_scholarship_id: null,
        discovered_at: serverTimestamp(),
        reviewed_at: null,
        reviewed_by: null,
        notes: notes.trim() || null,
      });
      nav('/inbox');
    } catch (e) {
      console.error('Capture save failed', e);
      setError('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  if (user === null) {
    return <div className="text-soft">Redirecting to sign in…</div>;
  }
  if (!membership) {
    return <div className="text-soft">Loading your household…</div>;
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <PageHeader
        title="Capture Scholarship"
        description={
          isPrefilled
            ? 'Review the details from the page and save to your Inbox for triage.'
            : 'Paste a scholarship link and add the details to save it to your Inbox.'
        }
        showApplicant={false}
      />

      {!isPrefilled && (
        <Card className="p-3 bg-forest-50 border-forest-200">
          <p className="text-sm text-forest-900">
            On your phone? Copy the scholarship's link from your browser, paste
            it into the URL field below, then fill in the details.
          </p>
        </Card>
      )}

      <Card className="p-4 space-y-4">
        <div>
          <Label htmlFor="cap-applicant">Applicant</Label>
          <Select
            id="cap-applicant"
            value={applicantId}
            onChange={(e) => setApplicantId(e.target.value)}
          >
            {applicants.length === 0 && <option value="">No applicants in this household</option>}
            {applicants.map((a) => (
              <option key={a.applicant_id} value={a.applicant_id}>
                {a.display_name}
              </option>
            ))}
          </Select>
          <HelperText>Which family member is this scholarship for?</HelperText>
        </div>

        <div>
          <Label htmlFor="cap-name">Name</Label>
          <Input
            id="cap-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Scholarship name"
            autoFocus={isPrefilled}
          />
        </div>

        <div>
          <Label htmlFor="cap-sponsor">Sponsor</Label>
          <Input
            id="cap-sponsor"
            value={sponsor}
            onChange={(e) => setSponsor(e.target.value)}
            placeholder="Sponsoring organization"
          />
        </div>

        <div>
          <Label htmlFor="cap-url">{isPrefilled ? 'URL' : 'Scholarship link'}</Label>
          <Input
            id="cap-url"
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={isPrefilled ? '' : 'Paste the scholarship page link here'}
            autoFocus={!isPrefilled}
          />
          <HelperText>
            {isPrefilled
              ? 'The page this scholarship was captured from.'
              : 'Paste the link you copied from your browser. Optional, but handy later.'}
          </HelperText>
        </div>

        {/* v0.2.19: amount / deadline / requirements fields the extension can pre-fill.
            v0.2.28: stacks on narrow (mobile) screens, two columns on wider. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cap-amount-min">Amount min ($)</Label>
            <Input
              id="cap-amount-min"
              type="number"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <Label htmlFor="cap-amount-max">Amount max ($)</Label>
            <Input
              id="cap-amount-max"
              type="number"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="cap-amount-notes">Amount notes</Label>
          <Input
            id="cap-amount-notes"
            value={amountNotes}
            onChange={(e) => setAmountNotes(e.target.value)}
            placeholder='e.g. "Varies", "Full Ride"'
          />
        </div>

        <div>
          <Label htmlFor="cap-deadline">Deadline</Label>
          <Input
            id="cap-deadline"
            value={deadlineApprox}
            onChange={(e) => setDeadlineApprox(e.target.value)}
            placeholder='e.g. "May 31, 2026"'
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-soft">
            <input
              type="checkbox"
              checked={deadlineRolling}
              onChange={(e) => setDeadlineRolling(e.target.checked)}
            />
            Rolling / monthly deadline
          </label>
        </div>

        <div>
          <Label htmlFor="cap-requirements">Requirements summary</Label>
          <Textarea
            id="cap-requirements"
            rows={2}
            value={requirementsSummary}
            onChange={(e) => setRequirementsSummary(e.target.value)}
            placeholder="Essay/no essay, eligibility, # winners, etc."
          />
        </div>

        <div>
          <Label htmlFor="cap-notes">Notes</Label>
          <Textarea
            id="cap-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any details worth keeping from the page (selected text, key eligibility, etc.)"
          />
        </div>

        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => nav('/')}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            <X size={14} className="mr-1" />Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            <Check size={14} className="mr-1" />
            {saving ? 'Saving…' : 'Save to Inbox'}
          </Button>
        </div>
      </Card>

      <HelperText>
        After saving, this item appears in your Inbox. From there you can review and promote it to a full scholarship, or dismiss if it's not relevant.
      </HelperText>
    </div>
  );
}
